import React from 'react';

/**
 * One operator table, two presentations — because a dense grid and a 390px
 * screen are genuinely different problems.
 *
 * Above `md` this renders a real <table>, which is what a person scanning
 * forty rows of rent or applications actually needs. Below it, each row
 * becomes a card with its column labels attached, so nothing has to be
 * horizontally scrolled to be read. The row's primary column leads the card
 * and its status sits beside it; everything else is a labelled pair.
 *
 * Callers describe the data once. They cannot produce a desktop-only table
 * by accident.
 */

export interface Column<Row> {
  /** Column heading. Also becomes the field label on the mobile card. */
  header: string;
  /** Cell content. */
  cell: (row: Row) => React.ReactNode;
  /** Marks the column that identifies the row — it leads the mobile card. */
  primary?: boolean;
  /** Marks the status/outcome column — it sits beside the primary on mobile. */
  status?: boolean;
  /** Right-align in the table (used for amounts). */
  align?: 'left' | 'right';
  /** Hide from the mobile card when the primary line already carries it. */
  hideOnCard?: boolean;
  className?: string;
}
interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Rendered in place of the table when there is nothing to show. */
  empty?: React.ReactNode;
  onRowClick?: (row: Row) => void;
}
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  onRowClick
}: DataTableProps<Row>) {
  if (rows.length === 0 && empty) return <>{empty}</>;
  const primary = columns.find(column => column.primary) ?? columns[0];
  const status = columns.find(column => column.status);
  const rest = columns.filter(column => column !== primary && column !== status && !column.hideOnCard);
  return <>
      {/* Desktop: a real table. */}
      <div className="hidden overflow-x-auto md:block" data-id="element-5182" data-mp="2e0xmdnc0g5j2">
        <table className="w-full text-left" data-id="element-5183" data-mp="c4gdvq8hx92k">
          <caption className="sr-only" data-id="element-5184" data-mp="c5hu094dclr5">{caption}</caption>
          <thead data-id="element-5185" data-mp="h3ji58gnjb9h">
            <tr className="border-b border-line" data-id="element-5186" data-mp="h42v0arg39gn">
              {columns.map(column => <th key={column.header} scope="col" className={`whitespace-nowrap px-3 py-3 text-caption font-semibold uppercase tracking-wide text-ink-muted ${column.align === 'right' ? 'text-right' : ''}`} data-id="element-5187" data-mp="2f6m6bhkdsez">
                  {column.header}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-5188" data-mp="3fm8a5gto0f6k">
            {rows.map(row => <tr key={rowKey(row)} onClick={onRowClick ? () => onRowClick(row) : undefined} className={onRowClick ? 'cursor-pointer hover:bg-canvas' : undefined} data-id="element-5189" data-mp="3kjgn92zyht0y">
                {columns.map(column => <td key={column.header} className={`px-3 py-3 align-top text-body-sm text-ink ${column.align === 'right' ? 'text-right' : ''} ${column.className ?? ''}`} data-id="element-5190" data-mp="33ao3ytlptat6">
                    {column.cell(row)}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Mobile: the same rows as readable cards, never a squeezed grid. */}
      <ul className="divide-y divide-line md:hidden" data-id="element-5191" data-mp="3i0d7uwl6zsx5">
        {rows.map(row => <li key={rowKey(row)} className="p-4" data-id="element-5192" data-mp="3vzlcw641dv99">
            <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-5193" data-mp="3vya3nqrtnvar">
              <p className="min-w-0 text-body font-medium text-ink" data-id="element-5194" data-mp="3r1jrhej51jum">{primary.cell(row)}</p>
              {status && <div className="shrink-0" data-id="element-5195" data-mp="21mn27lefcwle">{status.cell(row)}</div>}
            </div>
            {rest.length > 0 && <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-5196" data-mp="1wo1znfbpa0ts">
                {rest.map(column => <div key={column.header} className="min-w-0" data-id="element-5197" data-mp="1y3eiya2dqxj8">
                    <dt className="text-caption text-ink-subtle" data-id="element-5198" data-mp="231pr8gd10zzc">{column.header}</dt>
                    <dd className="text-body-sm text-ink" data-id="element-5199" data-mp="281atp0renqgt">{column.cell(row)}</dd>
                  </div>)}
              </dl>}
            {onRowClick && <button type="button" onClick={() => onRowClick(row)} className="mt-3 min-h-[44px] text-body-sm font-medium text-brand-700 hover:underline" data-id="element-5200" data-mp="1fdew7tu0huiz">
                Open
              </button>}
          </li>)}
      </ul>
    </>;
}